import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI } from '../../utils/fetchData';

const MetricCard = ({ title, value, subtitle, color = '#9932CC' }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
        <div className="flex items-center gap-2 mb-2">
            <div 
                className="w-[8px] h-[8px] rounded-full"
                style={{ backgroundColor: color }}
            />
            <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                {title}
            </span>
        </div>
        <div className="text-white text-[18px] font-bold mb-1">{value}</div>
        {subtitle && (
            <div className="text-[rgba(255,255,255,0.5)] text-[10px]">{subtitle}</div>
        )}
    </div>
);

const ContentCard = ({ title, children }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
        <div className="flex items-center gap-2 mb-4">
            <div className="w-[8px] h-[8px] rounded-full bg-[#9932CC]" />
            <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                {title}
            </span>
        </div>
        {children}
    </div>
);

const MerchAnalytics = () => {
    const navigate = useNavigate();
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const token = localStorage.getItem('dizeeToken');

    useEffect(() => {
        loadAnalytics();
    }, [filter]);

    const loadAnalytics = async () => {
        try {
            setLoading(true);
            const response = await getDataAPI(`user/analytics/merch?filter=${filter}`, token);
            setAnalyticsData(response.data.data.response);
        } catch (error) {
            console.error('Failed to load analytics:', error);
            setAnalyticsData(null);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount || isNaN(amount)) return '$0';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatNumber = (num) => {
        if (!num || isNaN(num)) return '0';
        return new Intl.NumberFormat('en-US').format(num);
    };

    if (loading) {
        return (
            <LayoutHeader>
                <div className="w-[390px] h-screen bg-black text-white flex items-center justify-center">
                    <p className="text-[12px]">Loading analytics...</p>
                </div>
            </LayoutHeader>
        );
    }

    return (
        <LayoutHeader>
            <div className="w-[390px] h-screen bg-black text-white relative">
                <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between">
                        <div className="flex items-center gap-2">
                            <div 
                                className="w-[24px] h-[24px] rounded-md"
                                style={{ backgroundColor: '#9932CC' }}
                            />
                            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Merch Analytics</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <p className='text-[#9932CC] cursor-pointer text-[12px] hover:opacity-70 transition-opacity'>Export data</p>
                            <div 
                                className="text-white cursor-pointer text-[12px] hover:text-gray-400 transition-colors"
                                onClick={() => navigate('/analytics/hub')}
                            >
                                Back
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="w-full px-4 flex-1 overflow-y-auto pb-[100px]">
                            {/* Filter Dropdown */}
                            <div className="mb-6">
                                <select 
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white text-[12px] px-4 py-3 rounded-lg focus:outline-none focus:border-[#9932CC] transition-colors appearance-none cursor-pointer"
                                    style={{
                                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='white' stroke-opacity='0.5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'right 1rem center'
                                    }}
                                >
                                    <option value="all" className="bg-black">All Time</option>
                                    <option value="day" className="bg-black">Last 24 Hours</option>
                                    <option value="week" className="bg-black">Last Week</option>
                                    <option value="month" className="bg-black">Last Month</option>
                                    <option value="year" className="bg-black">Last Year</option>
                                </select>
                            </div>

                        {analyticsData ? (
                            <>
                                {/* Metrics Grid */}
                                <div className="grid grid-cols-2 gap-4 mb-6">
                                    <MetricCard 
                                        title="Total Revenue" 
                                        value={formatCurrency(analyticsData.totalRevenue)}
                                        subtitle="Gross sales"
                                    />
                                    <MetricCard 
                                        title="Total Orders" 
                                        value={formatNumber(analyticsData.totalOrders)}
                                        subtitle={`${analyticsData.conversionRate}% conversion`}
                                    />
                                    <MetricCard 
                                        title="Units Sold" 
                                        value={formatNumber(analyticsData.totalUnits)}
                                        subtitle="Total items"
                                    />
                                    <MetricCard 
                                        title="Avg Order Value" 
                                        value={formatCurrency(analyticsData.averageOrderValue)}
                                        subtitle="Per order"
                                    />
                                </div>

                                {/* Chart Placeholder */}
                                <div className="bg-[#1A1A1A] rounded-lg p-4 border border-[#333] mb-6">
                                    <h3 className="text-white text-[12px] mb-3">Sales Trends</h3>
                                    <div className="h-[120px] bg-[#0A0A0A] rounded flex items-center justify-center">
                                        <p className="text-gray-500 text-[10px]">CHART</p>
                                    </div>
                                </div>

                                {/* Financial Breakdown */}
                                {analyticsData?.financialBreakdown && (
                                    <ContentCard title="Financial Breakdown">
                                        <div className="space-y-3">
                                            <div className="flex justify-between items-center">
                                                <span className="text-[10px] text-gray-400">Gross Sales</span>
                                                <span className="text-[10px] text-white">{formatCurrency(analyticsData.financialBreakdown.grossSales)}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-[10px] text-gray-400">Refunds</span>
                                                <span className="text-[10px] text-red-400">-{formatCurrency(analyticsData.financialBreakdown.refunds)}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-[10px] text-gray-400">Shipping</span>
                                                <span className="text-[10px] text-white">{formatCurrency(analyticsData.financialBreakdown.shipping)}</span>
                                            </div>
                                            <div className="w-full h-px bg-[rgba(255,255,255,0.1)]"></div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-[10px] text-white font-semibold">Net Revenue</span>
                                                <span className="text-[10px] text-green-400 font-semibold">{formatCurrency(analyticsData.financialBreakdown.netRevenue)}</span>
                                            </div>
                                        </div>
                                    </ContentCard>
                                )}

                                {/* Inventory Status */}
                                {analyticsData?.inventoryStatus && (
                                    <div className="mt-4">
                                        <ContentCard title="Inventory Status">
                                            <div className="space-y-3">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-[10px] text-gray-400">Active</span>
                                                    <span className="text-[10px] text-white">{analyticsData.inventoryStatus.active} products</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-[10px] text-gray-400">Draft</span>
                                                    <span className="text-[10px] text-white">{analyticsData.inventoryStatus.draft} products</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-[10px] text-gray-400">Archived</span>
                                                    <span className="text-[10px] text-white">{analyticsData.inventoryStatus.archived} products</span>
                                                </div>
                                            </div>
                                        </ContentCard>
                                    </div>
                                )}

                                {/* Category Breakdown */}
                                {analyticsData?.categoryBreakdown && analyticsData.categoryBreakdown.length > 0 && (
                                    <div className="mt-4">
                                        <ContentCard title="Product Categories">
                                            <div className="space-y-3">
                                                {analyticsData.categoryBreakdown.map((category, index) => (
                                                    <div key={index} className="flex justify-between items-center">
                                                        <span className="text-[10px] text-white">{category.category}</span>
                                                        <span className="text-[10px] text-gray-400">{category.products} products</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </ContentCard>
                                    </div>
                                )}

                                {/* Vendor Performance */}
                                {analyticsData?.vendorPerformance && analyticsData.vendorPerformance.length > 0 && (
                                    <div className="mt-4">
                                        <ContentCard title="Top Vendors/Brands">
                                            <div className="space-y-3">
                                                {analyticsData.vendorPerformance.map((vendor, index) => (
                                                    <div key={index} className="flex justify-between items-center">
                                                        <span className="text-[10px] text-white">{vendor.vendor}</span>
                                                        <span className="text-[10px] text-gray-400">{vendor.products} products</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </ContentCard>
                                    </div>
                                )}

                                {/* Top Products */}
                                {analyticsData?.topProducts && analyticsData.topProducts.length > 0 && (
                                    <div className="mt-4">
                                        <ContentCard title="Top Performing Products">
                                            <div className="space-y-3">
                                                {analyticsData.topProducts.map((product, index) => (
                                                    <div key={index} className="flex justify-between items-center">
                                                        <div>
                                                            <span className="text-[10px] text-white">{product.title}</span>
                                                            <span className="text-[8px] text-gray-400 ml-2">{product.clickCount} clicks</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </ContentCard>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="py-[40px]">
                                <h3 className="text-white text-[12px] font-normal mb-2">No merch data available</h3>
                                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6">
                                    Import your merchandise data to see detailed analytics.
                                </p>
                                <button 
                                    onClick={() => navigate('/merch')}
                                    className="bg-[#9932CC] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[#7A28A3] transition-colors"
                                >
                                    Import Merch
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Help Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 h-[100px] bg-black flex items-end justify-center pb-4">
                        <p className="text-gray-400 text-[10px] text-center px-4">
                            Track merchandise sales, inventory levels, and product performance based on your actual merch data.
                        </p>
                    </div>
                </div>
            </div>
        </LayoutHeader>
    );
};

export default MerchAnalytics; 